import React, { FC } from 'react';

import { Row } from 'antd';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { IRootReducers } from 'src/reducers';
import { ChallengeTypeEnum, ResourceTypeEnum } from 'src/shared/enums';
import {
  IChallengeResourceRender,
  IExperienceProgress,
} from 'src/shared/models';
import { isConPlatform } from 'utils';

const { EXPERIENCE_ACTIVATION, EXPERIENCE_PROGRESS, TEXT, CHECKOUT } =
  ResourceTypeEnum;

const ProgressBar = (props: { progress: number }) => {
  const { progress } = props;

  return (
    <div
      className={`experience-progress__container ${
        progress >= 1 ? 'in-progress' : ''
      }`}
    >
      <div
        className="experience-progress__filler"
        style={{ width: `${progress}%` }}
      >
        <p
          className="experience-progress__points small-body"
          style={{ left: `${progress > 5 ? progress - 5 : 0}%` }}
        >
          {progress}%
        </p>
      </div>
    </div>
  );
};

interface OwnProps extends IChallengeResourceRender {
  config: IExperienceProgress;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    user: state.auth.user,
  };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ChallengeExperienceProgress: FC<Props> = ({ challenge, config }) => {
  const { formatMessage } = useIntl();
  const { challengeResources, challengeType, isChallengeCompleted } =
    challenge || {};
  const { fullProgress, halfProgress, noProgress, idResource } = config;

  const isExperience = challengeType === ChallengeTypeEnum.EXPERIENCE;
  if (!config || !isExperience) return <React.Fragment />;

  const steps = challengeResources.filter(
    ({ type }) =>
      ![CHECKOUT, EXPERIENCE_ACTIVATION, EXPERIENCE_PROGRESS, TEXT].includes(
        type
      )
  );

  const completedResources = steps.filter(
    (resource) => resource.resource.usersM2MList.length > 0
  );

  const progress = Math.round((completedResources.length / steps.length) * 100);

  const currentActions = steps.length - completedResources.length;

  const currentProgressDescription =
    progress === 100
      ? fullProgress
      : progress >= 50
      ? halfProgress
      : noProgress;

  let progressDescription = currentProgressDescription
    .replace(
      '{acciones}',
      `${
        currentActions > 1
          ? `quedan ${currentActions} acciones`
          : `queda ${currentActions} acción`
      }`
    )
    .replace(
      '{actions}',
      `${
        currentActions > 1
          ? `${currentActions} actions`
          : `${currentActions} action`
      }`
    );

  if (isChallengeCompleted)
    progressDescription = formatMessage({
      id: 'page.experience.checkout.completed',
    });

  const progressClasses: string = classNames('experience-progress', {
    'experience-completed': progress === 100,
    'experience-completed-con': isConPlatform && progress === 100,
  });

  const titleClassName: string = classNames('regular-body title', {
    'experience-completed__title': isChallengeCompleted,
  });

  return (
    <div className={progressClasses}>
      {isConPlatform && progress === 100 && (
        <Row justify="center" align="middle">
          <div className="icon icon--fireworks" />
        </Row>
      )}
      <p className={titleClassName}>{progressDescription}</p>
      <ProgressBar key={idResource} progress={progress} />
    </div>
  );
};

export default connect(mapStateToProps)(ChallengeExperienceProgress);
